import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

const RestaurantSettings = ({ restaurant, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: restaurant.name || '',
    logo_url: restaurant.logo_url || '',
    primary_color: restaurant.primary_color || '#ee5b2b',
    promo_text: restaurant.promo_text || '',
    promo_active: restaurant.promo_active || false,
    instagram: restaurant.social_links?.instagram || '',
    facebook: restaurant.social_links?.facebook || '',
    latitude: restaurant.latitude || null,
    longitude: restaurant.longitude || null,
    geofence_radius: restaurant.geofence_radius || 50
  });
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  // --- PRO CHECK ---
  const isPro = restaurant.is_pro;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    let finalLogoUrl = formData.logo_url;

    // 1. Upload Logo (Only if Pro and file selected)
    if (isPro && logoFile) {
      const fileName = `logos/${restaurant.id}-${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage.from('models').upload(fileName, logoFile);
      if (uploadError) { alert("Logo upload failed"); setSaving(false); return; }
      const { data: { publicUrl } } = supabase.storage.from('models').getPublicUrl(fileName);
      finalLogoUrl = publicUrl;
    }

    // 2. Prepare Updates
    const updates = {
      name: formData.name,
      promo_text: formData.promo_text,
      promo_active: formData.promo_active,
      social_links: { instagram: formData.instagram, facebook: formData.facebook },
      latitude: formData.latitude,
      longitude: formData.longitude,
      geofence_radius: formData.geofence_radius
    };

    // Only update Pro fields if allowed
    if (isPro) {
      updates.logo_url = finalLogoUrl;
      updates.primary_color = formData.primary_color;
    }

    // 3. Update DB
    const { data, error } = await supabase
      .from('restaurants')
      .update(updates)
      .eq('id', restaurant.id)
      .select()
      .single();

    if (error) alert(error.message);
    else { onUpdate(data); onClose(); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-fade-in">

        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Restaurant Settings</h2>
            <div className="flex gap-2 items-center mt-1">
              <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${restaurant?.is_ultimate ? 'bg-gradient-to-r from-gray-900 via-purple-900 to-violet-900 text-white border border-purple-500/30' : (isPro ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-500')}`}>
                {restaurant?.is_ultimate ? 'ULTIMATE PLAN' : (isPro ? 'PRO PLAN' : 'FREE PLAN')}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black font-bold text-2xl">✕</button>
        </div>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto p-8">
          <form onSubmit={handleSave} className="space-y-6">

            {/* 1. BRANDING (LOCKED FOR FREE) */}
            <section className="space-y-4 border-b border-gray-100 pb-6">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                🎨 Brand Identity {!isPro && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">Locked 🔒</span>}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo Upload */}
                <div className={`relative p-4 border-2 border-dashed rounded-xl text-center ${!isPro ? 'opacity-50 pointer-events-none bg-gray-50' : 'border-gray-300'}`}>
                  {!isPro && <div className="absolute inset-0 flex items-center justify-center font-bold text-gray-400 text-sm z-10">Pro Feature</div>}
                  <label className="block text-sm font-bold text-gray-500 mb-2">Restaurant Logo</label>
                  {formData.logo_url && <img src={formData.logo_url} className="h-16 w-16 mx-auto mb-2 object-contain" alt="Logo" />}
                  <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" />
                </div>

                {/* Theme Color */}
                <div className={`relative p-4 border rounded-xl ${!isPro ? 'opacity-50 pointer-events-none bg-gray-50' : 'border-gray-200'}`}>
                  {!isPro && <div className="absolute inset-0 flex items-center justify-center font-bold text-gray-400 text-sm z-10">Pro Feature</div>}
                  <label className="block text-sm font-bold text-gray-500 mb-2">Primary Theme Color</label>
                  <div className="flex gap-3 items-center">
                    <input type="color" value={formData.primary_color} onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })} className="h-10 w-10 rounded cursor-pointer border-none" />
                    <span className="font-mono text-gray-500 text-sm">{formData.primary_color}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. GENERAL INFO (OPEN FOR ALL) */}
            <section className="space-y-4 border-b border-gray-100 pb-6">
              <h3 className="font-bold text-gray-900">🏠 General Info</h3>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Restaurant Name</label>
                <input type="text" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none font-medium" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Instagram URL</label>
                  <input type="text" placeholder="https://instagram.com/..." className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none text-sm" value={formData.instagram} onChange={e => setFormData({ ...formData, instagram: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Facebook URL</label>
                  <input type="text" placeholder="https://facebook.com/..." className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none text-sm" value={formData.facebook} onChange={e => setFormData({ ...formData, facebook: e.target.value })} />
                </div>
              </div>
            </section>

            {/* 3. PROMO BANNER (OPEN FOR ALL) */}
            <section className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-gray-900">📣 Announcement Banner</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={formData.promo_active} onChange={e => setFormData({ ...formData, promo_active: e.target.checked })} />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
              <input type="text" placeholder="e.g. 10% OFF on all Burgers today!" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none font-medium" value={formData.promo_text} onChange={e => setFormData({ ...formData, promo_text: e.target.value })} />
            </section>

            {/* 4. LOCATION & GEOFENCING (OPEN FOR ALL) */}
            <section className="space-y-4 border-b border-gray-100 pb-6">
              <h3 className="font-bold text-gray-900">📍 Location & Geofencing</h3>
              <p className="text-xs text-gray-500">Set your restaurant's location to prevent spam orders from people not at your restaurant.</p>

              {formData.latitude && formData.longitude ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <span className="text-green-700 font-bold text-sm">✅ Location Set</span>
                    <p className="text-xs text-green-600 mt-1">{formData.latitude.toFixed(5)}, {formData.longitude.toFixed(5)}</p>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, latitude: null, longitude: null })} className="text-xs text-red-500 font-bold hover:underline">Remove</button>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <span className="text-yellow-700 font-bold text-sm">⚠️ No location set — geofencing inactive</span>
                  <p className="text-xs text-yellow-600 mt-1">Orders can be placed from anywhere.</p>
                </div>
              )}

              <button
                type="button"
                disabled={locationLoading}
                onClick={() => setShowLocationDialog(true)}
                className="w-full py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2"
              >
                {locationLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Detecting...</>
                ) : (
                  <><span>📍</span> {formData.latitude ? 'Update My Location' : 'Set My Location'}</>
                )}
              </button>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Geofence Radius: {formData.geofence_radius}m</label>
                <input
                  type="range" min="20" max="500" step="10"
                  value={formData.geofence_radius}
                  onChange={e => setFormData({ ...formData, geofence_radius: parseInt(e.target.value) })}
                  className="w-full accent-orange-500"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>20m</span><span>250m</span><span>500m</span>
                </div>
              </div>
            </section>

            {/* Footer Actions */}
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
              <button disabled={saving} className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors shadow-lg">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

          </form>
        </div>
      </div>
      {/* LOCATION PERMISSION DIALOG */}
      {showLocationDialog && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white text-center">
              <div className="text-4xl mb-3">📍</div>
              <h3 className="text-xl font-bold">Enable Precise Location</h3>
              <p className="text-white/80 text-sm mt-2">For accurate geofencing, we need your precise GPS location.</p>
            </div>

            <div className="p-5">
              {isIOS ? (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-900 text-sm">Before tapping Continue:</p>
                  <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>Settings → Privacy → Location Services</strong></li>
                    <li>Make sure Location Services is <strong>ON</strong></li>
                    <li>Scroll to <strong>Safari Websites</strong> → set to <strong>"While Using"</strong></li>
                    <li>Enable <strong>"Precise Location"</strong></li>
                  </ol>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <p className="font-bold text-gray-900 text-sm">Before tapping Continue:</p>
                  <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                    <li>Open <strong>Settings → Location</strong></li>
                    <li>Make sure Location is <strong>ON</strong></li>
                    <li>Set mode to <strong>"High Accuracy"</strong> or <strong>"Precise"</strong></li>
                    <li>When the browser asks, tap <strong>"Allow"</strong> and select <strong>"Precise"</strong></li>
                  </ol>
                </div>
              )}
            </div>

            <div className="p-4 pt-0 flex gap-3">
              <button type="button" onClick={() => setShowLocationDialog(false)} className="flex-1 py-3 bg-gray-100 text-gray-600 font-bold rounded-xl text-sm hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={() => {
                setShowLocationDialog(false);
                setLocationLoading(true);
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    setFormData({ ...formData, latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                    setLocationLoading(false);
                  },
                  (err) => {
                    alert('Could not get location. Please check your device settings and try again.');
                    setLocationLoading(false);
                  },
                  { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                );
              }} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition-colors shadow-lg">
                Continue →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantSettings;